export const NODE_RESPONSIBILITY_HISTORY_LIMIT = 50;
export const TASK_COMPLETION_HISTORY_LIMIT = 50;

function isoTimestamp(now) {
  const value = typeof now === 'function' ? now() : now;
  return (value instanceof Date ? value : new Date(value || Date.now())).toISOString();
}

function boundedHistory(history, entry, limit) {
  const existing = Array.isArray(history) ? history : [];
  return [...existing.slice(-(limit - 1)), entry];
}

function humanPrincipal(value) {
  if (!value?.userId) return null;
  return {
    type: 'human',
    userId: String(value.userId).slice(0, 128),
    displayName: String(value.displayName || 'Mindmappi collaborator').trim().slice(0, 160)
      || 'Mindmappi collaborator',
  };
}

export function normalizeTaskAssignees(members) {
  return (Array.isArray(members) ? members : [])
    .filter((member) => member?.role === 'editor' && member?.userId)
    .map((member) => ({
      userId: member.userId,
      displayName: member.displayName || 'Mindmappi collaborator',
      selectionLabel: member.displayName || member.email || 'Mindmappi collaborator',
    }))
    .sort((left, right) => (
      left.selectionLabel.localeCompare(right.selectionLabel, undefined, { sensitivity: 'base' })
      || left.userId.localeCompare(right.userId)
    ));
}

export function canCompleteAssignedTask({
  node,
  canWriteMap,
  accessRole,
  currentUserId,
  allowUnattributed = false,
}) {
  if (!node?.isTask || !canWriteMap) return false;
  if (accessRole === 'owner') return true;
  if (!currentUserId) {
    return allowUnattributed && !node.userId && !node.responsiblePrincipal?.userId;
  }
  return (
    (!node.userId && !node.responsiblePrincipal?.userId)
    ||
    node.userId === currentUserId
    || node.responsiblePrincipal?.userId === currentUserId
  );
}

export function buildResponsibilityPatch({ node, assignee, actorUserId, now = Date.now }) {
  if (!node?.id || !actorUserId) throw new Error('A node and assigning user are required');
  const at = isoTimestamp(now);
  const previousPrincipal = humanPrincipal(node.responsiblePrincipal);
  const responsiblePrincipal = humanPrincipal(assignee);
  const historyEntry = responsiblePrincipal
    ? {
        action: 'assigned',
        principal: responsiblePrincipal,
        at,
        byUserId: actorUserId,
      }
    : {
        action: 'unassigned',
        principal: previousPrincipal,
        at,
        byUserId: actorUserId,
      };
  return {
    responsiblePrincipal,
    responsibilityAssignedAt: responsiblePrincipal ? at : null,
    responsibilityAssignedByUserId: responsiblePrincipal ? actorUserId : null,
    responsibilityHistory: boundedHistory(
      node.responsibilityHistory,
      historyEntry,
      NODE_RESPONSIBILITY_HISTORY_LIMIT
    ),
    lastEditedBy: actorUserId,
  };
}

export function buildTaskCompletionPatch({ node, done, actorUserId, now = Date.now }) {
  if (!node?.id || !node.isTask || !actorUserId) {
    throw new Error('A task node and completing user are required');
  }
  const at = isoTimestamp(now);
  return {
    isTask: true,
    taskDone: Boolean(done),
    taskCompletedAt: done ? at : null,
    taskCompletedByUserId: done ? actorUserId : null,
    taskCompletionHistory: boundedHistory(
      node.taskCompletionHistory,
      {
        done: Boolean(done),
        at,
        byUserId: actorUserId,
      },
      TASK_COMPLETION_HISTORY_LIMIT
    ),
    lastEditedBy: actorUserId,
  };
}
